import { useCallback, useEffect, useRef, useState } from 'react';
import { GameFrame, PromptLine, shuffle, splitFace, useGame, useRoundTimer, type GameProps } from '../kit/shell';
import { sortPrompt } from '../phrases';
import { Chip, DropSpot, usePlacer } from '../kit/place';
import { KINDS } from '../../concepts/kinds';
import Pic from '../../media/Pic';

type Bin = { label: string; items: string[] };
type Card = { text: string; bin: string };

export default function SortBins(props: GameProps) {
  const game = useGame(props);
  const bins = ((game.config?.sequences ?? []) as Bin[]).filter((b) => b?.label && Array.isArray(b.items) && b.items.length > 0);
  const perRound = game.num('itemsPerRound', 6);
  const speakCards = game.bool('chipSpeech', true);
  const [queue, setQueue] = useState<Card[]>([]);
  const [current, setCurrent] = useState<Card | null>(null);
  const [trial, setTrial] = useState<string | null>(null);
  const [sorted, setSorted] = useState<Record<string, string[]>>({});
  const [bounce, setBounce] = useState(false);
  const [done, setDone] = useState(false);
  const busy = useRef(false);

  const present = useCallback(async (card: Card | undefined, rest: Card[]) => {
    if (!card) { setCurrent(null); setDone(true); return; }
    const id = await game.register({ item: card.text, bins: bins.map((b) => b.label) }, { value: card.bin });
    if (!id) return;
    setCurrent(card);
    setQueue(rest);
    setTrial(id);
  }, [bins.map((b) => b.label).join()]);

  const start = useCallback(async () => {
    if (bins.length < 2) return;
    setDone(false);
    setSorted({});
    const all = shuffle(bins.flatMap((b) => b.items.map((text) => ({ text, bin: b.label })))).slice(0, perRound);
    await present(all[0], all.slice(1));
  }, [bins.map((b) => `${b.label}:${b.items.join()}`).join('|'), perRound, present]);

  useEffect(() => { if (game.config) void start(); }, [game.config, start]);
  useEffect(() => {
    if (!current) return;
    if (!game.textPrompt) game.say(sortPrompt(splitFace(current.text).word));
  }, [current, trial]);

  const placer = usePlacer((_c, bin) => { void drop(bin); });

  async function drop(bin: string) {
    if (!current || !trial || busy.current) return;
    if (game.errorless && bin !== current.bin) { setBounce(true); window.setTimeout(() => setBounce(false), 400); return; }
    busy.current = true;
    const v = await game.respond(trial, bin);
    if (v?.correct) {
      setSorted((s) => ({ ...s, [bin]: [...(s[bin] ?? []), current.text] }));
      busy.current = false;
      await present(queue[0], queue.slice(1));
      return;
    }
    setBounce(true);
    window.setTimeout(() => setBounce(false), 400);
    const fresh = await game.register({ item: current.text, bins: bins.map((b) => b.label), retry: true }, { value: current.bin });
    busy.current = false;
    if (fresh) setTrial(fresh);
  }

  const fading = useRoundTimer(done, game.transitionMs, () => { void start(); }, 1200);

  return (
    <GameFrame
      game={game}
      empty={bins.length < 2}
      emptyText={`${KINDS.sortBins.describes} Edit it to add at least two groups.`}
      onResume={() => { void start(); }}
      breakTrial={trial}
    >
      <div className={`g-board g-sort${fading ? ' is-fading' : ''}`}>
        <PromptLine game={game} text={current ? `Where does it go?` : 'All sorted!'} onRepeat={() => current && game.say(sortPrompt(splitFace(current.text).word))} />
        <div className="g-bins" style={{ gridTemplateColumns: `repeat(${bins.length}, minmax(0, 1fr))` }}>
          {bins.map((b) => {
            const f = splitFace(b.label);
            return (
              <DropSpot
                key={b.label}
                placer={placer}
                id={b.label}
                label={f.word}
                className="is-bin"
                inert={game.errorless && !!placer.held && current?.bin !== b.label}
              >
                <span className="g-bin-head">
                  <Pic text={b.label} fallback={f.picture ? <span className="g-bin-pic" aria-hidden="true">{f.picture}</span> : null} className="g-bin-img" />
                  <span className="g-bin-word">{f.word}</span>
                </span>
                <span className="g-bin-items" aria-hidden="true">
                  {(sorted[b.label] ?? []).map((t, i) => <span key={i}>{splitFace(t).picture || splitFace(t).word}</span>)}
                </span>
              </DropSpot>
            );
          })}
        </div>
        <div className="g-tray" role="group" aria-label="Card to sort">
          {current && (() => {
            const f = splitFace(current.text);
            return (
              <Chip
                key={queue.length}
                placer={placer}
                id="card"
                label={f.word}
                className={`is-big${bounce ? ' is-bounce' : ''}`}
                onTap={() => { if (speakCards) game.say(f.word); }}
              >
                <Pic text={current.text} fallback={f.picture ? <span className="g-chip-pic g-chip-emoji" aria-hidden="true">{f.picture}</span> : null} className="g-chip-pic" />
                <span className="g-chip-word">{f.word}</span>
              </Chip>
            );
          })()}
        </div>
      </div>
    </GameFrame>
  );
}
